import * as React from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@relay-towing/tokens';
import {
  rateFormulaBulletRecipe,
  rateFormulaCardRecipe,
  type RateFormulaBulletRecipeProps,
} from '../recipes/rate-formula.recipe';
import { IconTile } from './Icon';
import { Inline } from './Layout';
import { Text } from './Text';

/** The glyph's colour for each tile tone; a neutral tile takes a muted glyph. */
const GLYPH_TONE = {
  neutral: 'muted',
  primary: 'primary',
  success: 'success',
  warning: 'warning',
  info: 'info',
  destructive: 'destructive',
} as const;

export interface RateFormulaCardProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  icon: LucideIcon;
  /** Colours the tile and the bullet together. */
  tone?: NonNullable<RateFormulaBulletRecipeProps['tone']>;
  title: React.ReactNode;
  /** The formula. Emphasise a term with `<Text as="span" variant="body-sm-strong">`. */
  children: React.ReactNode;
}

/**
 * One of the two "here is how the charge is built" panels on the Rates tab.
 *
 *   <RateFormulaCard icon={Phone} tone="success" title="Calls">
 *     <Text as="span" variant="body-sm-strong">Tow Base</Text> +
 *     <Text as="span" variant="body-sm-strong"> Hourly Rate</Text> after 1st hour
 *   </RateFormulaCard>
 *
 * The tile and the bullet share one tone so the legend and the title agree
 * without the page choosing two colours. The formula itself is copy, and
 * stays with the page.
 */
export const RateFormulaCard = React.forwardRef<
  HTMLDivElement,
  RateFormulaCardProps
>(function RateFormulaCard(
  { icon, tone = 'neutral', title, className, children, ...props },
  ref
) {
  return (
    <div
      ref={ref}
      className={cn(rateFormulaCardRecipe(), className)}
      {...props}
    >
      <Inline gap={2} align="center">
        <IconTile
          icon={icon}
          size={7}
          shape="circle"
          tone={tone}
          iconTone={GLYPH_TONE[tone]}
        />
        <Text variant="body-sm-strong" as="span">
          {title}
        </Text>
      </Inline>
      <Inline gap={2} align="center">
        <span
          className={rateFormulaBulletRecipe({ tone })}
          aria-hidden="true"
        />
        <Text variant="body-sm" tone="muted">
          {children}
        </Text>
      </Inline>
    </div>
  );
});
